import { Field, Form, Formik } from "formik";
import { cloneDeep, get, keys, range, toPairs } from "lodash";
import moment, { Moment } from "moment";
import * as React from "react";
import * as Yup from "yup";
import { THEME } from "../../../theme/ThemeStore";
import { t } from "../../../i18n/util";
import { hrStore } from "../../../stores/HrStore";
import { IApplicationStateEnum, IPostApplicationStateTransitionPayload } from "../../../types/models";
import { useMobileMediaQuery } from "../../hooks/useMobileMediaQuery";
import { useUsers } from "../../hooks/useUsers";
import { CustomAutoComplete } from "../../ui/CustomAutoComplete";
import { CustomCheckbox } from "../../ui/CustomCheckbox";
import { CustomDatePicker } from "../../ui/CustomDatePicker";
import { CustomInputField } from "../../ui/CustomInputField";
import { CustomTimePicker } from "../../ui/CustomTimePicker";
import { ErrorMessagesList, FormButtons } from "../../ui/Primitives";
import _ from "lodash";
import { templateToString, transitionToTemplate } from "../../util/helpers";
import { authStore } from "../../../stores/AuthStore";

// helper variable to map date suggestions
const DATE_SUGGESTION_COUNT = range(1, 4);

type Props = {
    onSubmit: (model: IPostApplicationStateTransitionPayload) => void;
    onBack: () => void;
    toApplicationState: IApplicationStateEnum;
    fromApplicationState: IApplicationStateEnum;
    firstName: string;
};

export const KanbanTransferInterviewDialog = ({
    onSubmit,
    onBack,
    toApplicationState,
    fromApplicationState,
    firstName
}: Props) => {
    const users = useUsers()?.map((user) => user.username);
    const isMobile = useMobileMediaQuery();

    return (
        <Formik
            initialValues={hrStore.currentDialogForm}
            onSubmit={onSubmit}
            validationSchema={Yup.object().shape({
                title: Yup.string(),
                interviewDate1: Yup.date()
                    .nullable()
                    .typeError(t("error.date"))
                    .min(moment().toDate(), t("error.date")),
                interviewStartTime1: Yup.date().nullable().typeError(t("error.time")),
                interviewEndTime1: Yup.date().nullable().typeError(t("error.time")),
                interviewDate2: Yup.date()
                    .nullable()
                    .typeError(t("error.date"))
                    .min(moment().toDate(), t("error.date")),
                interviewStartTime2: Yup.date().nullable().typeError(t("error.time")),
                interviewEndTime2: Yup.date().nullable().typeError(t("error.time")),
                interviewDate3: Yup.date()
                    .nullable()
                    .typeError(t("error.date"))
                    .min(moment().toDate(), t("error.date")),
                interviewStartTime3: Yup.date().nullable().typeError(t("error.time")),
                interviewEndTime3: Yup.date().nullable().typeError(t("error.time")),
                guests: Yup.array().of(Yup.string()).min(1, t("error.requireAppointmentSuggestionGuests")),
                onlineMeeting: Yup.boolean()
            })}
            validateOnChange
            validateOnBlur
            validate={(values) => {
                const errors: Record<string, string> = {};

                const interviewKeyToError = (key: string) => {
                    if (key.startsWith("interviewDate")) {
                        return t("error.requireAppointmentSuggestionDate");
                    } else if (key.startsWith("interviewStartTime")) {
                        return t("error.requireAppointmentSuggestionStartTime");
                    } else if (key.startsWith("interviewEndTime")) {
                        return t("error.requireAppointmentSuggestionEndTime");
                    }
                };

                DATE_SUGGESTION_COUNT.forEach((number) => {
                    const interviewData = keys(values).filter(
                        (key) => key.startsWith("interview") && key.includes(number.toString())
                    );

                    if (
                        interviewData.some((key) => get(values, key)) &&
                        !interviewData.every((key) => get(values, key))
                    ) {
                        interviewData
                            .filter((key) => !get(values, key))
                            .forEach((key) => {
                                const error = interviewKeyToError(key);
                                if (error) {
                                    errors[key] = error;
                                }
                            });
                    }
                });

                if (toPairs(errors).length === 0) {
                    if (
                        !(
                            (values.interviewDate1 && values.interviewStartTime1 && values.interviewEndTime1) ||
                            (values.interviewDate2 && values.interviewStartTime2 && values.interviewEndTime2) ||
                            (values.interviewDate3 && values.interviewStartTime3 && values.interviewEndTime3)
                        )
                    ) {
                        keys(values)
                            .filter((key) => key.startsWith("interview"))
                            .forEach((key) => {
                                if (!get(values, key)) {
                                    errors[key] = t("error.requireAppointmentSuggestion");
                                }
                            });
                    }
                }

                const startTimeKeyForIndex = (index: number) =>
                    `interviewStartTime${index}` as keyof IPostApplicationStateTransitionPayload;

                const startTimeForIndex = (index: number) => values[startTimeKeyForIndex(index)] as Date;

                const endTimeKeyForIndex = (index: number) =>
                    `interviewEndTime${index}` as keyof IPostApplicationStateTransitionPayload;

                const endTimeForIndex = (index: number) => values[endTimeKeyForIndex(index)] as Date;

                const dateKeyForIndex = (index: number) =>
                    `interviewDate${index}` as keyof IPostApplicationStateTransitionPayload;

                const dateForIndex = (index: number) => values[dateKeyForIndex(index)] as Date;

                // HRB-443: Validate end time after start time
                DATE_SUGGESTION_COUNT.forEach((i) => {
                    const startTime = startTimeForIndex(i);
                    const endTime = endTimeForIndex(i);
                    if (startTime && endTime && endTime < startTime) {
                        // Overlap -> mark end time with error
                        errors[endTimeKeyForIndex(i)] = t("error.appointment.endBeforeStart");
                    }
                });

                // HRB-446: Validate no overlapping time slots
                for (let i = 1; i < DATE_SUGGESTION_COUNT.length; ++i) {
                    const lhsDate = dateForIndex(i);
                    const lhsStartTime = startTimeForIndex(i);
                    const lhsEndTime = endTimeForIndex(i);

                    if (!lhsDate || !lhsStartTime || !lhsEndTime) {
                        continue;
                    }

                    for (let j = i + 1; j < DATE_SUGGESTION_COUNT.length + 1; ++j) {
                        const rhsDate = dateForIndex(j);
                        const rhsStartTime = startTimeForIndex(j);
                        const rhsEndTime = endTimeForIndex(j);

                        if (!rhsDate || !rhsStartTime || !rhsEndTime) {
                            continue;
                        }

                        // Check is same date
                        if (moment(lhsDate).isSame(rhsDate, "day")) {
                            // Check if time ranges overlap
                            if (
                                (moment(lhsStartTime).isBefore(rhsStartTime) &&
                                    moment(lhsEndTime).isAfter(rhsStartTime)) ||
                                (moment(lhsStartTime).isAfter(rhsStartTime) &&
                                    moment(lhsStartTime).isBefore(rhsEndTime))
                            ) {
                                // Overlapping time slots -> mark relevant fields with error
                                errors[dateKeyForIndex(i)] = t("error.appointment.overlapping");
                                errors[startTimeKeyForIndex(i)] = t("error.appointment.overlapping");
                                errors[endTimeKeyForIndex(i)] = t("error.appointment.overlapping");
                                errors[dateKeyForIndex(j)] = t("error.appointment.overlapping");
                                errors[startTimeKeyForIndex(j)] = t("error.appointment.overlapping");
                                errors[endTimeKeyForIndex(j)] = t("error.appointment.overlapping");
                            }
                        }
                    }
                }

                return errors;
            }}
        >
            {({ errors, touched, setFieldValue, values }) => (
                <Form
                    style={{
                        flexGrow: 1,
                        display: "flex",
                        minHeight: 540,
                        padding: 24,
                        flexDirection: "column",
                        gap: 16
                    }}
                >
                    <Field
                        component={CustomInputField}
                        label={t("screen.dashboard.dialog.title")}
                        name="title"
                        style={{ width: "100%" }}
                    />
                    {DATE_SUGGESTION_COUNT.map((number) => (
                        <div
                            key={number}
                            style={{
                                width: "100%",
                                display: "flex",
                                flexDirection: isMobile ? "column" : "row",
                                alignItems: "center",
                                gap: 16
                            }}
                        >
                            <Field
                                component={CustomDatePicker}
                                placeholder={`${t("screen.dashboard.dialog.dateSuggestion")} ${number}`}
                                middleText
                                name={`interviewDate${number}`}
                                style={{ width: "100%", flex: 3 }}
                            />
                            {/* div is needed for mobile styling */}
                            <div style={{ display: "flex", flex: 4, gap: 16, alignItems: "center" }}>
                                <Field
                                    component={CustomTimePicker}
                                    placeholder={t("screen.dashboard.dialog.timeFromPlaceholder")}
                                    middleText
                                    name={`interviewStartTime${number}`}
                                    style={{ flex: 3 }}
                                    onAccept={(date: Moment) => {
                                        // timeout is needed to prevent bug where validation would display errors despite correct input
                                        setTimeout(
                                            () =>
                                                setFieldValue(
                                                    `interviewEndTime${number}`,
                                                    cloneDeep(date).add(1, "hours")
                                                ),
                                            1
                                        );
                                    }}
                                    onBlur={() => {
                                        if (
                                            !get(errors, `interviewStartTime${number}`) &&
                                            get(values, `interviewStartTime${number}`)
                                        ) {
                                            setFieldValue(
                                                `interviewEndTime${number}`,
                                                cloneDeep(get(values, `interviewStartTime${number}`)).add(1, "hours")
                                            );
                                        }
                                    }}
                                />
                                <div
                                    style={{
                                        color: THEME.customColors.headingsDark,
                                        textAlign: "center"
                                    }}
                                    className="body2"
                                >
                                    {t("screen.dashboard.dialog.until")}
                                </div>
                                <Field
                                    component={CustomTimePicker}
                                    placeholder={t("screen.dashboard.dialog.timeToPlaceholder")}
                                    middleText
                                    name={`interviewEndTime${number}`}
                                    style={{ flex: 3 }}
                                />
                            </div>
                        </div>
                    ))}
                    <Field
                        component={CustomAutoComplete}
                        // HRB-444: filter out selected guests from list
                        options={users?.filter((u) => !values.guests?.includes(u)) ?? []}
                        placeholder={t("screen.dashboard.dialog.addGuests")}
                        name="guests"
                        setFieldValue={setFieldValue}
                        style={{ width: "100%" }}
                        value={values.guests}
                    />
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            marginTop: 10,
                            color: THEME.customColors.headingsDark
                        }}
                        className="body1"
                    >
                        <Field
                            type="checkbox"
                            name="onlineMeeting"
                            checked={values.onlineMeeting}
                            component={CustomCheckbox}
                            onChange={(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                                setFieldValue(
                                    "template",
                                    transitionToTemplate(fromApplicationState, toApplicationState, undefined, checked)
                                );
                                setFieldValue(
                                    "message",
                                    templateToString(
                                        values.language,
                                        transitionToTemplate(
                                            fromApplicationState,
                                            toApplicationState,
                                            undefined,
                                            checked
                                        ),
                                        { firstName, userName: authStore.givenName ?? "" }
                                    )
                                );
                            }}
                        />
                        {t("screen.dashboard.dialog.onlineMeeting")}
                    </div>
                    <ErrorMessagesList errors={errors} touched={touched} style={{ marginTop: 0 }} />
                    <FormButtons
                        labelBack={t("screen.dataUpload.dialog.cancel")}
                        labelSubmit={t("screen.motivation.next")}
                        onBack={onBack}
                        style={{
                            justifyContent: "flex-end",
                            marginBottom: 0
                        }}
                    />
                </Form>
            )}
        </Formik>
    );
};
